import type { Equipment } from '../api/active/generated/equipment';
import type { PackageDetail } from '../api/active/generated/package';
import type {
  PlanDetail,
  PlanPackageItemDetail,
} from '../api/active/generated/plan';
import type {
  CustomerLinkConfiguration,
  CustomerLinkConfigurationRequest,
  CustomerLinkReadResult,
  ExactQuoteVersionTuple,
  FrozenProjectionSnapshot,
  InventoryFreshnessBinding,
  InventoryWarning,
  PublishCustomerShareRequest,
  QuoteColumnSnapshot,
  QuoteGroup,
  QuoteManagementPage,
  QuoteManagementRecord,
  QuoteMasterRecord,
  QuoteVersionNode,
  QuoteVersionTree,
  QuoteWorkspaceAggregate,
  QuoteWorkspaceItem,
  QuoteWorkspaceItemWrite,
  SaveQuoteDraftRequest,
} from '../api/active/generated/quote';
import { QUOTE_SOURCE_FRAGMENT_VERSION } from '../domain/quoteSourceFragment';
import { isStableBusinessModeCode } from '../pages/cpq-v2/state/businessModes';
import type { OutputTermsDisplayOverrides } from '../pages/cpq-v2/state/outputTerms';
import type {
  BusinessModeCode,
  FormalQuoteColumnSnapshot,
  QuoteContainerInstance,
  QuoteContainerInstanceWrite,
  QuoteContainerOriginKind,
  QuoteContainerOriginSnapshot,
  QuoteLocalPlanDefinition,
  QuoteLocalPlanDefinitionMember,
  QuoteLocalPlanInstance,
  QuoteLocalPlanInstanceMember,
  QuoteWorkspaceGroupState,
  StableBusinessModeCode,
} from '../pages/cpq-v2/state/types';
import { quoteLocalPackageDefinitionWrite } from './quoteLocalDefinitionWrite';
import { CONFIRMED_QUOTE_READONLY_MESSAGE } from './quoteManagementSummary';
import type { QuoteScopeAction } from './quoteScopeAuthorization';

export class QuoteMapperError extends Error {}

export type CustomerLinkModel = {
  id: string;
  quoteId: string;
  customerId: string;
  status: 'active' | 'disabled' | 'expired' | 'revoked';
  effectiveExpiresAt: string;
  configuration: CustomerLinkConfiguration;
  resourceVersion: string;
  publications: {
    id: string;
    customerVersionNo: string;
    quoteVersion: QuoteVersionTupleModel;
    createdAt: string;
  }[];
  currentPublication: {
    id: string;
    customerVersionNo: string;
    quoteVersion: QuoteVersionTupleModel;
  };
};

export type CustomerLinkDirectoryEntryModel = {
  link: CustomerLinkModel;
  current: boolean;
};

function mapCustomerLinkValue(
  link: NonNullable<CustomerLinkReadResult['link']>,
): CustomerLinkModel {
  return {
    id: link.id,
    quoteId: link.quoteId,
    customerId: link.boundCustomerId,
    status: link.status,
    effectiveExpiresAt: link.effectiveExpiresAt,
    configuration: { ...link.configuration },
    resourceVersion: link.resourceVersion,
    publications: link.publications.map((entry) => ({
      id: entry.id,
      customerVersionNo: entry.customerVersionNo,
      quoteVersion: { ...entry.quoteVersion },
      createdAt: entry.createdAt,
    })),
    currentPublication: {
      id: link.currentPublication.id,
      customerVersionNo: link.currentPublication.customerVersionNo,
      quoteVersion: { ...link.currentPublication.quoteVersion },
    },
  };
}

// Deliberately exclude the authorized URL: it stays only in the open dialog.
export function mapCustomerLink(
  value: CustomerLinkReadResult,
): CustomerLinkModel | null {
  const link = value.link;
  if (!link) return null;
  return mapCustomerLinkValue(link);
}

// URLs are intentionally omitted: callers may keep them only for the visible,
// authorized link-management surface and must clear them when it closes.
export function mapCustomerLinkDirectory(
  value: CustomerLinkReadResult,
): CustomerLinkDirectoryEntryModel[] {
  return value.links.map((entry) => ({
    link: mapCustomerLinkValue(entry.link),
    current: entry.current,
  }));
}

export function customerVersionLabel(
  link: CustomerLinkModel | null,
  lifecycle: 'draft' | 'published',
): string {
  if (!link) return '未发布草稿';
  const current = Number(link.currentPublication.customerVersionNo);
  return lifecycle === 'draft' ? `V${current + 1} 草稿` : `V${current}`;
}

const quoteContainerOriginKinds = new Set<QuoteContainerOriginKind>([
  'catalog-package',
  'catalog-plan',
  'quote-local-package',
  'quote-local-plan',
  'session-package',
  'session-plan',
]);

function mapContainerOriginSnapshot(
  value: unknown,
): QuoteContainerOriginSnapshot {
  if (!value || typeof value !== 'object')
    throw new QuoteMapperError('报价容器来源快照缺失');
  const candidate = value as Record<string, unknown>;
  if (
    !(candidate.name === undefined || typeof candidate.name === 'string') ||
    !(
      candidate.revisionId === null || typeof candidate.revisionId === 'string'
    ) ||
    !(
      candidate.resourceVersion === null ||
      typeof candidate.resourceVersion === 'string'
    ) ||
    !Array.isArray(candidate.members)
  )
    throw new QuoteMapperError('报价容器来源快照格式无效');
  const members = candidate.members.map((raw) => {
    if (!raw || typeof raw !== 'object')
      throw new QuoteMapperError('报价容器来源成员快照格式无效');
    const member = raw as Record<string, unknown>;
    if (
      typeof member.memberId !== 'string' ||
      typeof member.sourceKind !== 'string' ||
      typeof member.sourceId !== 'string' ||
      !(
        member.sourceRevisionId === null ||
        typeof member.sourceRevisionId === 'string'
      ) ||
      !(
        member.resourceVersion === null ||
        typeof member.resourceVersion === 'string'
      ) ||
      typeof member.quantity !== 'number' ||
      !Number.isSafeInteger(member.quantity) ||
      member.quantity < 1
    )
      throw new QuoteMapperError('报价容器来源成员快照格式无效');
    return {
      memberId: member.memberId,
      sourceKind: member.sourceKind,
      sourceId: member.sourceId,
      sourceRevisionId: member.sourceRevisionId,
      resourceVersion: member.resourceVersion,
      quantity: member.quantity,
    };
  });
  const planPricing = candidate.planPricing as
    | Record<string, unknown>
    | undefined;
  const validPlanPricing =
    planPricing?.version === 'catalog-plan-derived-discount-v1' &&
    typeof planPricing.sourceRevisionId === 'string' &&
    planPricing.sourceRevisionId === candidate.revisionId &&
    (planPricing.derivedDiscountPercent === null ||
      (typeof planPricing.derivedDiscountPercent === 'string' &&
        planPricing.derivedDiscountPercent.trim() !== '' &&
        Number.isFinite(Number(planPricing.derivedDiscountPercent)) &&
        Number(planPricing.derivedDiscountPercent) >= 0));
  return {
    ...(validPlanPricing
      ? {
          planPricing: {
            version: 'catalog-plan-derived-discount-v1' as const,
            sourceRevisionId: planPricing.sourceRevisionId as string,
            derivedDiscountPercent: planPricing.derivedDiscountPercent as
              | string
              | null,
          },
        }
      : {}),
    ...(typeof candidate.name === 'string' ? { name: candidate.name } : {}),
    revisionId: candidate.revisionId,
    resourceVersion: candidate.resourceVersion,
    members,
  };
}

function mapContainerInstance(value: unknown): QuoteContainerInstance {
  if (!value || typeof value !== 'object')
    throw new QuoteMapperError('报价容器实例格式无效');
  const candidate = value as Record<string, unknown>;
  const originSnapshot = mapContainerOriginSnapshot(candidate.originSnapshot);
  if (
    typeof candidate.id !== 'string' ||
    !candidate.id ||
    (candidate.kind !== 'package' && candidate.kind !== 'plan') ||
    typeof candidate.originKind !== 'string' ||
    !quoteContainerOriginKinds.has(
      candidate.originKind as QuoteContainerOriginKind,
    ) ||
    typeof candidate.originId !== 'string' ||
    !candidate.originId ||
    !(
      candidate.originRevisionId === null ||
      typeof candidate.originRevisionId === 'string'
    ) ||
    !(
      candidate.originResourceVersion === undefined ||
      candidate.originResourceVersion === null ||
      typeof candidate.originResourceVersion === 'string'
    ) ||
    !(
      candidate.parentInstanceId === null ||
      typeof candidate.parentInstanceId === 'string'
    ) ||
    !(
      candidate.parentMemberId === null ||
      typeof candidate.parentMemberId === 'string'
    ) ||
    typeof candidate.quantity !== 'number' ||
    !Number.isSafeInteger(candidate.quantity) ||
    candidate.quantity < 1 ||
    !(
      candidate.planDiscountSnapshot === null ||
      typeof candidate.planDiscountSnapshot === 'string'
    ) ||
    (candidate.status !== 'pristine' && candidate.status !== 'customized')
  )
    throw new QuoteMapperError('报价容器实例字段格式无效');
  const result: QuoteContainerInstanceWrite = {
    id: candidate.id,
    kind: candidate.kind,
    displayName:
      typeof candidate.displayName === 'string' && candidate.displayName.trim()
        ? candidate.displayName
        : (originSnapshot.name ?? ''),
    originKind: candidate.originKind as QuoteContainerOriginKind,
    originId: candidate.originId,
    originRevisionId: candidate.originRevisionId,
    ...(candidate.originResourceVersion === undefined
      ? {}
      : { originResourceVersion: candidate.originResourceVersion }),
    parentInstanceId: candidate.parentInstanceId,
    parentMemberId: candidate.parentMemberId,
    quantity: candidate.quantity,
    planDiscountSnapshot: candidate.planDiscountSnapshot,
    status: candidate.status,
  };
  return {
    ...result,
    originSnapshot,
  };
}

export function customerShareRequest(input: {
  workspace: QuoteWorkspaceModel;
  items: readonly QuoteWorkspaceDraftItem[];
  groups?: readonly QuoteGroupModel[];
  localPlanInstances?: readonly LocalPlanInstanceModel[];
  containerEditMode?: 'legacy_unbind' | 'quote_edit';
  containerInstances?: readonly QuoteContainerInstance[];
  pricingView: 'purchase' | 'rental';
  businessModeCode: StableBusinessModeCode;
  quoteDiscountPercent: string;
  customerId: string;
  quoteExpiresAt: string;
  link: CustomerLinkModel | null;
  replaceCurrentLink?: boolean;
  configuration: CustomerLinkConfigurationRequest;
  marketingDescription?: string;
  marketingDescriptionEdited?: boolean;
  outputTermsDisplayOverrides?: OutputTermsDisplayOverrides;
  outputTermsRequired?: boolean;
}): PublishCustomerShareRequest {
  if (input.workspace.masterLocked)
    throw new QuoteMapperError(CONFIRMED_QUOTE_READONLY_MESSAGE);
  if (!input.customerId) throw new QuoteMapperError('客户分享前请选择客户');
  if (
    !input.quoteExpiresAt ||
    !Number.isFinite(Date.parse(input.quoteExpiresAt))
  )
    throw new QuoteMapperError('客户分享前请填写报价有效期');
  if (input.link && input.customerId !== input.link.customerId)
    throw new QuoteMapperError('首次分享后客户已锁定');
  const suppliedGroups = input.groups ?? input.workspace.groups;
  const preservedGroups = [
    ...suppliedGroups.map((group) => {
      const workspaceGroup = input.workspace.groups.find(
        (candidate) => candidate.id === group.id,
      );
      const preservedWorkspaceGroup = workspaceGroup
        ? { ...workspaceGroup }
        : undefined;
      if (preservedWorkspaceGroup && group.kind === 'display') {
        delete preservedWorkspaceGroup.definitionId;
        delete preservedWorkspaceGroup.members;
      }
      return {
        ...preservedWorkspaceGroup,
        ...group,
        ...(group.kind !== 'display' &&
        group.definitionId === undefined &&
        workspaceGroup
          ? { definitionId: workspaceGroup.definitionId }
          : {}),
        ...(group.planInstanceId === undefined && workspaceGroup
          ? { planInstanceId: workspaceGroup.planInstanceId }
          : {}),
        ...(group.planMemberId === undefined && workspaceGroup
          ? { planMemberId: workspaceGroup.planMemberId }
          : {}),
        ...(group.kind !== 'display' &&
        group.members === undefined &&
        workspaceGroup?.members
          ? { members: workspaceGroup.members }
          : {}),
      };
    }),
    ...input.workspace.groups.filter(
      (group) => !suppliedGroups.some((candidate) => candidate.id === group.id),
    ),
  ];
  const common = {
    customerId: input.customerId,
    quoteExpiresAt: input.quoteExpiresAt,
    draftTuple: exactVersionTuple(input.workspace.selectedVersion),
    draftSave: saveDraftRequest({ ...input, groups: preservedGroups }),
    publicationConfiguration: {
      channel: input.configuration.channel,
      linkExpiresAt: input.configuration.linkExpiresAt,
      styleId: input.configuration.styleId,
      fieldCombinationId: input.configuration.fieldCombinationId,
    },
  };
  return input.link
    ? {
        ...common,
        mode:
          input.replaceCurrentLink || input.link.status === 'revoked'
            ? 'replace-link-publish'
            : 'update-publish',
        expectedCurrentPublication: {
          publicationId: input.link.currentPublication.id,
          customerVersionNo: input.link.currentPublication.customerVersionNo,
        },
      }
    : { ...common, mode: 'first-publish', expectedCurrentPublication: null };
}

export type QuoteVersionTupleModel = {
  quoteId: string;
  quoteVersionId: string;
  quoteVersionNo: string;
  resourceVersion: string;
};

export type QuoteInventoryItemModel = {
  quoteItemId: string;
  deviceId: string;
  warehouseId: string;
  inventoryFreshnessRunId: string | null;
  status: 'KNOWN' | 'SHORTAGE' | 'UNKNOWN';
  requiredQuantity: number;
  availableQuantity: string | null;
  observedAt: string | null;
  qualityReason: string | null;
};

export type QuoteInventoryModel = {
  inventoryFreshnessRunId: string | null;
  status: 'KNOWN' | 'SHORTAGE' | 'UNKNOWN';
  observedAt: string | null;
  items: QuoteInventoryItemModel[];
};

export type QuoteFrozenSnapshotModel = {
  revisionTuple: {
    quoteId: string;
    quoteVersionId: string;
    quoteVersionNo: number;
    sourceHash: string;
    calculationVersion: string;
    fieldProjectionVersion: string;
    templateVersion: string;
  };
  fieldProjection: {
    fields: FrozenProjectionSnapshot['fieldProjection']['fields'];
    channel:
      | 'public-share'
      | 'internal-share'
      | 'xlsx'
      | 'image'
      | 'pdf'
      | 'formal-document';
  };
  sourceSnapshot: Record<string, unknown>;
};

export type QuoteVersionLineageModel = {
  parentVersion: QuoteVersionTupleModel | null;
  sourceVersion: QuoteVersionTupleModel | null;
  rebasedFromDraftId: string | null;
  sourceWorkspaceImportId: string | null;
};

export type QuoteVersionModel = {
  id: string;
  quoteId: string;
  versionNo: string;
  lifecycle: 'draft' | 'published';
  containerEditMode: 'legacy_unbind' | 'quote_edit';
  containerEditModeSupported: boolean;
  containerInstances: QuoteContainerInstance[];
  businessModeCode?: StableBusinessModeCode | null;
  lineage?: QuoteVersionLineageModel;
  inventoryFreshness?: QuoteInventoryModel;
  pricingPolicyVersion?: string;
  contentContractVersion?: typeof QUOTE_SOURCE_FRAGMENT_VERSION | null;
  quoteExpiresAt?: string | null;
  pendingCustomerName?: string | null;
  frozenSnapshot?: QuoteFrozenSnapshotModel | null;
  /** 内部工作台展示偏好，不参与客户发布内容或其源指纹。 */
  outputTermsRequired?: boolean;
  resourceVersion: string;
  createdAt: string;
  deletedAt: string | null;
};

export const hasUnpersistedCustomizedQuoteContainer = (
  current: readonly QuoteContainerInstance[],
  persisted: readonly QuoteContainerInstance[],
): boolean => {
  const persistedCustomizedIds = new Set(
    persisted
      .filter((instance) => instance.status === 'customized')
      .map((instance) => instance.id),
  );
  return current.some(
    (instance) =>
      instance.status === 'customized' &&
      !persistedCustomizedIds.has(instance.id),
  );
};

export type QuoteWorkspaceItemModel = {
  id: string;
  resourceVersion?: string;
  sourceType: 'equipment' | 'package' | 'plan';
  sourceId: string;
  sourceRevisionId: string;
  packageNameSnapshot?: string | null;
  deviceId: string;
  sourceSnapshot: { name: string; code: string } & Record<string, unknown>;
  deviceFieldSnapshot: Record<string, unknown>;
  packageRelationId: string | null;
  packageId: string | null;
  packageItemPriceSnapshotCents: number | null;
  packageRelationQuantitySnapshot: number | null;
  planId: string | null;
  planDiscountSnapshot: string | null;
  planProductPriceSnapshotCents: number | null;
  replacementRelationId?: string | null;
  pricingSnapshot: Record<string, unknown>;
  nameSnapshot: string;
  quantity: number;
  unitPriceCents: number;
  discountPercent: string;
  lineTotalCents: number;
  sortOrder: number;
  remark: string | null;
  groupId: string | null;
  containerInstanceId?: string | null;
};

export type QuoteWorkspaceDraftItem = {
  id: string;
  sourceType: 'equipment' | 'package' | 'plan';
  sourceId: string;
  sourceRevisionId: string;
  deviceId: string;
  sourceSnapshot: { name: string; code: string } & Record<string, unknown>;
  deviceFieldSnapshot: Record<string, unknown>;
  packageRelationId: string | null;
  packageId: string | null;
  packageItemPriceSnapshotCents: number | null;
  packageRelationQuantitySnapshot: number | null;
  planId: string | null;
  planDiscountSnapshot: string | null;
  planProductPriceSnapshotCents: number | null;
  replacementRelationId?: string | null;
  quantity: number;
  discountPercent: string;
  sortOrder: number;
  remark: string | null;
  groupId: string | null;
  containerInstanceId?: string | null;
  unitPriceCents: number | null;
  lineTotalCents: number | null;
  exclusivePricingEdit?: 'exclusive_discount' | 'exclusive_price';
  exclusivePricingEditContext?: {
    itemIds: string[];
    totalLineCents: number;
  };
};

export type QuoteGroupModel = QuoteWorkspaceGroupState;

/**
 * Quote rows and live package containers determine group membership. Cached
 * group metadata may fill fields, but it cannot restore a removed package.
 * An empty package is still live when its container exists without any rows.
 */
export function isCurrentQuoteGroup(
  group: QuoteGroupModel,
  currentGroupIds: ReadonlySet<string>,
  itemGroupIds: ReadonlySet<string>,
  packageInstanceIds: ReadonlySet<string>,
): boolean {
  if (group.containerInstanceId)
    return packageInstanceIds.has(group.containerInstanceId);
  return currentGroupIds.has(group.id) || itemGroupIds.has(group.id);
}

export type QuoteLocalPackageDefinitionModel = {
  id: string;
  name: string;
  sortOrder: number;
  members: Array<{ deviceId: string; quantity: number }>;
  resourceVersion: string;
  /** New definitions always set this; missing on old snapshots remains unknown. */
  packageGroupId?: string | null;
  mergePricesOverride?: boolean | null;
  packageGroupSnapshot?:
    | import('../package-integration/packageGroups').PackageGroupSnapshot
    | null;
};

export type LocalPlanDefinitionMemberModel = QuoteLocalPlanDefinitionMember;
export type LocalPlanDefinitionModel = QuoteLocalPlanDefinition;
export type LocalPlanInstanceMemberModel = QuoteLocalPlanInstanceMember;
export type LocalPlanInstanceModel = QuoteLocalPlanInstance;

export type QuoteColumnModel = FormalQuoteColumnSnapshot;

export type QuoteOrderSummaryModel = {
  masterLocked: boolean;
  businessStatus: 'QUOTING' | 'ORDER_PROCESSING' | 'ORDER_FAILED' | 'ORDERED';
  confirmationId: string | null;
  confirmationQuoteVersionId: string | null;
  confirmationStatus: 'QUEUED' | 'SUCCEEDED' | 'FAILED' | null;
  deliveryStatus: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | null;
  customerManagerName: string | null;
};

export type QuoteMasterModel = {
  id: string;
  quoteNo: string;
  customerName?: string | null;
  customerId?: string | null;
  currentPublishedVersion: QuoteVersionTupleModel | null;
  draftCount: number;
  fixedShareLinkId?: string | null;
  archivedAt: string | null;
  resourceVersion: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type QuoteManagementRecordModel = QuoteMasterModel &
  QuoteOrderSummaryModel & {
    currentCustomerShare: {
      publicationId: string;
      customerVersionNo: string;
      quoteVersion: QuoteVersionTupleModel;
    } | null;
    currentShareAmount: { currency: 'CNY'; totalCents: number } | null;
    customerLinkStatus:
      | 'unshared'
      | 'active'
      | 'disabled'
      | 'expired'
      | 'revoked';
    customerName: string | null;
    createdByName: string | null;
    customerManagerUserId: string | null;
    createdBy: string | null;
    orderReady: boolean;
    state:
      | 'draft'
      | 'collaboration'
      | 'customer_confirmation'
      | 'pending_submission'
      | 'submitting'
      | 'success'
      | 'failed'
      | 'void'
      | 'archived';
    salesOrderId: string | null;
  };

export type QuoteConfirmationHistoryModel = {
  confirmationId: string;
  quoteVersionId: string;
  quoteVersionNo: string;
  confirmedAt: string;
  submissionStatus: 'QUEUED' | 'SUCCEEDED' | 'FAILED' | null;
  deliveryStatus: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | null;
};

export type QuoteWorkspaceModel = QuoteMasterModel &
  QuoteOrderSummaryModel & {
    containerEditModeSettings?: {
      enabled: boolean;
      resourceVersion: string;
    };
    authorization?: {
      fieldCapabilities: Array<{
        fieldKey: string;
        writable: boolean;
        writeDecision: 'allow' | 'deny';
      }>;
    };
    allowedActions: string[];
    scopeAllowedPermissions?: QuoteScopeAction[];
    confirmationHistory: QuoteConfirmationHistoryModel[];
    selectedVersion: QuoteVersionModel;
    pricingView: 'purchase' | 'rental';
    calculationSnapshot: {
      currency: 'CNY';
      discountPercent: string;
      modeOverallDiscounts?: Record<string, string>;
      totalCents: number;
    };
    items: QuoteWorkspaceItemModel[];
    groups: QuoteGroupModel[];
    localPackageDefinitions: QuoteLocalPackageDefinitionModel[];
    localPlanDefinitions?: LocalPlanDefinitionModel[];
    localPlanInstances?: LocalPlanInstanceModel[];
    columns: QuoteColumnModel[];
    selectableColumns?: QuoteColumnModel[];
  };

export function quoteFieldCapabilityCanWrite(
  authorization: QuoteWorkspaceModel['authorization'],
  fieldKey: string,
): boolean {
  return (
    authorization?.fieldCapabilities.some(
      (capability) =>
        capability.fieldKey === fieldKey &&
        capability.writable === true &&
        capability.writeDecision === 'allow',
    ) === true
  );
}

export function withQuoteWorkspaceContainerEditModeSettings(
  workspace: QuoteWorkspaceModel | null,
  settings: NonNullable<QuoteWorkspaceModel['containerEditModeSettings']>,
): QuoteWorkspaceModel | null {
  if (!workspace) return workspace;
  if (
    workspace.containerEditModeSettings &&
    BigInt(workspace.containerEditModeSettings.resourceVersion) >=
      BigInt(settings.resourceVersion)
  )
    return workspace;
  return { ...workspace, containerEditModeSettings: { ...settings } };
}

/** Preserve explicit historical modes; only new frozen purchase markers fill a missing publication mode. */
export function quoteWorkspaceBusinessModeCode(
  workspace: QuoteWorkspaceModel,
): StableBusinessModeCode {
  const savedMode = workspace.selectedVersion.businessModeCode;
  if (savedMode) return savedMode;
  const frozenBusinessMode =
    workspace.selectedVersion.frozenSnapshot?.sourceSnapshot.businessModeCode;
  const publicationPurchase =
    frozenBusinessMode === undefined &&
    workspace.pricingView === 'purchase' &&
    workspace.selectedVersion.frozenSnapshot?.sourceSnapshot.pricingView ===
      'purchase' &&
    workspace.items.length > 0 &&
    workspace.items.every(
      (item) => item.pricingSnapshot?.purchasePriceBasisVersion === 'tax13-v1',
    );
  return workspace.pricingView === 'rental'
    ? 'rental'
    : frozenBusinessMode === 'buyout' || publicationPurchase
      ? 'buyout'
      : 'full';
}

export type QuoteWarningModel = {
  code: 'INVENTORY_SHORTAGE' | 'INVENTORY_UNKNOWN';
  severity: 'WARNING';
  status: 'SHORTAGE' | 'UNKNOWN';
  inventoryFreshnessRunId?: string | null;
  reason?: string;
  items: QuoteInventoryItemModel[];
};

export type QuoteMutationModel = {
  quoteMaster: QuoteMasterModel;
  version: QuoteVersionModel;
  inventoryWarnings?: QuoteWarningModel[];
  replayed: boolean;
};

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    throw new QuoteMapperError(`${label} 不是对象`);
  return { ...(value as Record<string, unknown>) };
}

function requiredString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim().length === 0)
    throw new QuoteMapperError(`${label} 缺少有效值`);
  return value;
}

function sourceSnapshot(value: unknown): {
  name: string;
  code: string;
} & Record<string, unknown> {
  const snapshot = record(value, '报价来源快照');
  return {
    ...snapshot,
    name: requiredString(snapshot.name, '报价来源名称'),
    code: requiredString(snapshot.code, '报价来源编码'),
  };
}

function mapTuple(
  value: ExactQuoteVersionTuple | null,
): QuoteVersionTupleModel | null {
  if (value === null) return null;
  return {
    quoteId: value.quoteId,
    quoteVersionId: value.quoteVersionId,
    quoteVersionNo: value.quoteVersionNo,
    resourceVersion: value.resourceVersion,
  };
}

function mapInventory(value: InventoryFreshnessBinding): QuoteInventoryModel {
  return {
    inventoryFreshnessRunId: value.inventoryFreshnessRunId,
    status: value.status,
    observedAt: value.observedAt,
    items: value.items.map((item) => ({
      quoteItemId: item.quoteItemId,
      deviceId: item.deviceId,
      warehouseId: item.warehouseId,
      inventoryFreshnessRunId: item.inventoryFreshnessRunId,
      status: item.status,
      requiredQuantity: item.requiredQuantity,
      availableQuantity: item.availableQuantity,
      observedAt: item.observedAt,
      qualityReason: item.qualityReason,
    })),
  };
}

function mapFrozenSnapshot(
  value: FrozenProjectionSnapshot | null,
): QuoteFrozenSnapshotModel | null {
  if (value === null) return null;
  return {
    revisionTuple: {
      quoteId: value.revisionTuple.quoteId,
      quoteVersionId: value.revisionTuple.quoteVersionId,
      quoteVersionNo: value.revisionTuple.quoteVersionNo,
      sourceHash: value.revisionTuple.sourceHash,
      calculationVersion: value.revisionTuple.calculationVersion,
      fieldProjectionVersion: value.revisionTuple.fieldProjectionVersion,
      templateVersion: value.revisionTuple.templateVersion,
    },
    fieldProjection: {
      fields: [...value.fieldProjection.fields],
      channel: value.fieldProjection.channel,
    },
    sourceSnapshot: record(value.sourceSnapshot, '报价冻结来源快照'),
  };
}

function mapLineage(
  value: NonNullable<QuoteVersionNode['lineage']>,
): QuoteVersionLineageModel {
  return {
    parentVersion: mapTuple(value.parentVersion),
    sourceVersion: mapTuple(value.sourceVersion),
    rebasedFromDraftId: value.rebasedFromDraftId,
    sourceWorkspaceImportId: value.sourceWorkspaceImportId,
  };
}

export function mapQuoteVersionNode(
  value: QuoteVersionNode,
): QuoteVersionModel {
  const businessModeCode = value.businessModeCode;
  if (
    value.outputTermsRequired !== undefined &&
    typeof value.outputTermsRequired !== 'boolean'
  )
    throw new QuoteMapperError('工作台条款显示偏好格式无效');
  if (
    businessModeCode != null &&
    (typeof businessModeCode !== 'string' ||
      !isStableBusinessModeCode(businessModeCode as StableBusinessModeCode))
  )
    throw new QuoteMapperError('报价业务模式 code 不受当前客户端支持');
  const contentContractVersion = (
    value as QuoteVersionNode & { contentContractVersion?: string | null }
  ).contentContractVersion;
  const containerEditMode = (
    value as QuoteVersionNode & { containerEditMode?: unknown }
  ).containerEditMode;
  const rawContainerInstances = (
    value as QuoteVersionNode & { containerInstances?: unknown }
  ).containerInstances;
  if (
    containerEditMode !== undefined &&
    containerEditMode !== 'legacy_unbind' &&
    containerEditMode !== 'quote_edit'
  )
    throw new QuoteMapperError('套餐与方案编辑模式不受当前客户端支持');
  if (
    contentContractVersion != null &&
    contentContractVersion !== QUOTE_SOURCE_FRAGMENT_VERSION
  )
    throw new QuoteMapperError('报价内容版本暂不支持，请更新客户端');
  if (
    rawContainerInstances !== undefined &&
    !Array.isArray(rawContainerInstances)
  )
    throw new QuoteMapperError('报价容器实例集合格式无效');
  const containerInstances = Array.isArray(rawContainerInstances)
    ? rawContainerInstances.map(mapContainerInstance)
    : [];
  if (
    new Set(containerInstances.map((instance) => instance.id)).size !==
    containerInstances.length
  )
    throw new QuoteMapperError('报价容器实例标识重复');
  return {
    contentContractVersion: contentContractVersion as
      | typeof QUOTE_SOURCE_FRAGMENT_VERSION
      | null
      | undefined,
    id: value.id,
    quoteId: value.quoteId,
    versionNo: value.versionNo,
    lifecycle: value.lifecycle,
    containerEditMode:
      (containerEditMode as 'legacy_unbind' | 'quote_edit' | undefined) ??
      'legacy_unbind',
    containerEditModeSupported: containerEditMode !== undefined,
    containerInstances,
    businessModeCode: (businessModeCode ??
      null) as StableBusinessModeCode | null,
    lineage: value.lineage ? mapLineage(value.lineage) : undefined,
    inventoryFreshness: value.inventoryFreshness
      ? mapInventory(value.inventoryFreshness)
      : undefined,
    pricingPolicyVersion: value.pricingPolicyVersion,
    quoteExpiresAt: value.quoteExpiresAt,
    pendingCustomerName: value.pendingCustomerName,
    ...(value.outputTermsRequired === undefined
      ? {}
      : { outputTermsRequired: value.outputTermsRequired }),
    frozenSnapshot:
      value.frozenSnapshot === undefined
        ? undefined
        : mapFrozenSnapshot(value.frozenSnapshot),
    resourceVersion: value.resourceVersion,
    createdAt: value.createdAt,
    deletedAt: value.deletedAt,
  };
}

function mapOrderSummary(
  value: QuoteWorkspaceAggregate | QuoteManagementRecord,
): QuoteOrderSummaryModel {
  if (
    typeof value.masterLocked !== 'boolean' ||
    !value.businessStatus ||
    !['QUOTING', 'ORDER_PROCESSING', 'ORDER_FAILED', 'ORDERED'].includes(
      value.businessStatus,
    )
  )
    throw new QuoteMapperError('报价锁定和业务状态摘要缺失，请刷新后重试');
  return {
    masterLocked: value.masterLocked,
    businessStatus: value.businessStatus,
    confirmationId: value.confirmationId ?? null,
    confirmationQuoteVersionId: value.confirmationQuoteVersionId ?? null,
    confirmationStatus: value.confirmationStatus ?? null,
    deliveryStatus: value.deliveryStatus ?? null,
    customerManagerName: value.customerManagerName ?? null,
  };
}

function mapMaster(
  value: QuoteMasterRecord & Pick<QuoteWorkspaceAggregate, 'customerName'>,
): QuoteMasterModel {
  return {
    id: value.id,
    quoteNo: requiredString(value.quoteNo, '报价编号'),
    customerId: value.customerId,
    customerName: value.customerName,
    currentPublishedVersion: mapTuple(value.currentPublishedVersion),
    draftCount: value.draftCount,
    fixedShareLinkId: value.fixedShareLinkId,
    archivedAt: value.archivedAt,
    resourceVersion: value.resourceVersion,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    deletedAt: value.deletedAt,
  };
}

export function mapQuoteMasterRecord(
  value: QuoteMasterRecord,
): QuoteMasterModel {
  return mapMaster(value);
}

function mapWorkspaceItem(value: QuoteWorkspaceItem): QuoteWorkspaceItemModel {
  const extended = value as QuoteWorkspaceItem & {
    resourceVersion?: unknown;
    containerInstanceId?: unknown;
  };
  if (
    extended.containerInstanceId !== undefined &&
    extended.containerInstanceId !== null &&
    typeof extended.containerInstanceId !== 'string'
  )
    throw new QuoteMapperError('报价行容器实例关联格式无效');
  return {
    id: value.id,
    ...(typeof extended.resourceVersion === 'string'
      ? { resourceVersion: extended.resourceVersion }
      : {}),
    sourceType: value.sourceType,
    sourceId: value.sourceId,
    sourceRevisionId: value.sourceRevisionId,
    packageNameSnapshot: value.packageNameSnapshot,
    deviceId: value.deviceId,
    sourceSnapshot: sourceSnapshot(value.sourceSnapshot),
    deviceFieldSnapshot: record(value.deviceFieldSnapshot, '设备字段快照'),
    packageRelationId: value.packageRelationId,
    packageId: value.packageId,
    packageItemPriceSnapshotCents: value.packageItemPriceSnapshotCents,
    packageRelationQuantitySnapshot: value.packageRelationQuantitySnapshot,
    planId: value.planId,
    planDiscountSnapshot: value.planDiscountSnapshot,
    planProductPriceSnapshotCents: value.planProductPriceSnapshotCents,
    replacementRelationId: value.replacementRelationId ?? null,
    pricingSnapshot: record(value.pricingSnapshot, '报价定价快照'),
    nameSnapshot: requiredString(value.nameSnapshot, '报价行名称'),
    quantity: value.quantity,
    unitPriceCents: value.unitPriceCents,
    discountPercent: value.discountPercent,
    lineTotalCents: value.lineTotalCents,
    sortOrder: value.sortOrder,
    remark: value.remark,
    groupId: value.groupId,
    ...(typeof extended.containerInstanceId === 'string' ||
    extended.containerInstanceId === null
      ? { containerInstanceId: extended.containerInstanceId }
      : {}),
  };
}

function mapGroup(value: QuoteGroup): QuoteGroupModel {
  const extended = value as QuoteGroup & {
    kind?: unknown;
    members?: unknown;
    definitionId?: unknown;
    planInstanceId?: unknown;
    planMemberId?: unknown;
    containerInstanceId?: unknown;
  };
  const kind = extended.kind;
  if (
    (extended.planInstanceId === undefined) !==
      (extended.planMemberId === undefined) ||
    (extended.planInstanceId !== undefined &&
      extended.planInstanceId !== null &&
      typeof extended.planInstanceId !== 'string') ||
    (extended.planMemberId !== undefined &&
      extended.planMemberId !== null &&
      typeof extended.planMemberId !== 'string') ||
    (extended.containerInstanceId !== undefined &&
      extended.containerInstanceId !== null &&
      typeof extended.containerInstanceId !== 'string')
  )
    throw new QuoteMapperError('报价组方案实例关系不完整');
  if (
    (typeof extended.planInstanceId === 'string') !==
    (typeof extended.planMemberId === 'string')
  )
    throw new QuoteMapperError('报价组方案实例关系不完整');
  if (extended.members !== undefined && kind !== 'quote-local-package')
    throw new QuoteMapperError('非报价套餐分组包含套餐成员定义');
  const members = Array.isArray(extended.members)
    ? extended.members.map((member) => {
        if (!member || typeof member !== 'object')
          throw new QuoteMapperError('报价套餐成员定义格式无效');
        const candidate = member as Record<string, unknown>;
        if (
          typeof candidate.deviceId !== 'string' ||
          typeof candidate.quantity !== 'number' ||
          !Number.isSafeInteger(candidate.quantity) ||
          candidate.quantity < 1
        )
          throw new QuoteMapperError('报价套餐成员定义格式无效');
        return {
          deviceId: candidate.deviceId,
          quantity: candidate.quantity,
        };
      })
    : undefined;
  return {
    id: value.id,
    name: value.name,
    sortOrder: value.sortOrder,
    ...(kind === 'display' || kind === 'quote-local-package' ? { kind } : {}),
    ...(members ? { members } : {}),
    ...(typeof extended.definitionId === 'string' ||
    extended.definitionId === null
      ? { definitionId: extended.definitionId }
      : {}),
    ...(typeof extended.planInstanceId === 'string' ||
    extended.planInstanceId === null
      ? { planInstanceId: extended.planInstanceId }
      : {}),
    ...(typeof extended.planMemberId === 'string' ||
    extended.planMemberId === null
      ? { planMemberId: extended.planMemberId }
      : {}),
    ...(typeof extended.containerInstanceId === 'string' ||
    extended.containerInstanceId === null
      ? { containerInstanceId: extended.containerInstanceId }
      : {}),
  };
}

function mapLocalPlanDefinition(value: unknown): LocalPlanDefinitionModel {
  if (!value || typeof value !== 'object')
    throw new QuoteMapperError('报价内方案定义格式无效');
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.id !== 'string' ||
    candidate.id.length === 0 ||
    typeof candidate.name !== 'string' ||
    typeof candidate.sortOrder !== 'number' ||
    !Number.isSafeInteger(candidate.sortOrder) ||
    (typeof candidate.discountPercent !== 'string' &&
      typeof candidate.discountPercent !== 'number') ||
    !Number.isFinite(Number(candidate.discountPercent)) ||
    Number(candidate.discountPercent) < 0 ||
    typeof candidate.resourceVersion !== 'string' ||
    !/^[1-9][0-9]*$/.test(candidate.resourceVersion) ||
    !Array.isArray(candidate.members)
  )
    throw new QuoteMapperError('报价内方案定义缺少合同字段');
  const members = candidate.members.map(
    (member): LocalPlanDefinitionMemberModel => {
      if (!member || typeof member !== 'object')
        throw new QuoteMapperError('报价内方案成员格式无效');
      const entry = member as Record<string, unknown>;
      if (
        typeof entry.occurrenceId !== 'string' ||
        typeof entry.quantityPerSet !== 'number' ||
        !Number.isSafeInteger(entry.quantityPerSet) ||
        entry.quantityPerSet < 1 ||
        !entry.source ||
        typeof entry.source !== 'object'
      )
        throw new QuoteMapperError('报价内方案成员缺少合同字段');
      const source = entry.source as Record<string, unknown>;
      if (
        source.type === 'catalog-package' &&
        typeof source.packageId === 'string'
      )
        return {
          occurrenceId: entry.occurrenceId,
          quantityPerSet: entry.quantityPerSet,
          source: { type: 'catalog-package', packageId: source.packageId },
        };
      if (
        source.type === 'local-package' &&
        typeof source.definitionId === 'string'
      )
        return {
          occurrenceId: entry.occurrenceId,
          quantityPerSet: entry.quantityPerSet,
          source: { type: 'local-package', definitionId: source.definitionId },
        };
      throw new QuoteMapperError('报价内方案成员来源无效');
    },
  );
  if (
    new Set(members.map((member) => member.occurrenceId)).size !==
    members.length
  )
    throw new QuoteMapperError('报价内方案 occurrenceId 重复');
  return {
    id: candidate.id,
    name: candidate.name,
    sortOrder: candidate.sortOrder,
    discountPercent: Number(candidate.discountPercent),
    members,
    resourceVersion: candidate.resourceVersion,
    ...(typeof candidate.createdAt === 'string'
      ? { createdAt: candidate.createdAt }
      : {}),
    ...(typeof candidate.updatedAt === 'string'
      ? { updatedAt: candidate.updatedAt }
      : {}),
  };
}

export function mapQuoteLocalDefinitions(value: {
  localPackageDefinitions: readonly unknown[];
  localPlanDefinitions: readonly unknown[];
}): {
  localPackageDefinitions: QuoteLocalPackageDefinitionModel[];
  localPlanDefinitions: LocalPlanDefinitionModel[];
} {
  const localPackageDefinitions = value.localPackageDefinitions.map((raw) => {
    if (!raw || typeof raw !== 'object')
      throw new QuoteMapperError('报价内套餐定义格式无效');
    const entry = raw as Record<string, unknown>;
    if (
      typeof entry.id !== 'string' ||
      typeof entry.name !== 'string' ||
      typeof entry.sortOrder !== 'number' ||
      !Number.isSafeInteger(entry.sortOrder) ||
      typeof entry.resourceVersion !== 'string' ||
      !/^[1-9][0-9]*$/.test(entry.resourceVersion) ||
      !Array.isArray(entry.members)
    )
      throw new QuoteMapperError('报价内套餐定义字段无效');
    const packageGroupId = entry.packageGroupId;
    if (
      packageGroupId !== undefined &&
      packageGroupId !== null &&
      typeof packageGroupId !== 'string'
    )
      throw new QuoteMapperError('报价内套餐所属组合标识无效');
    const packageGroupSnapshot = entry.packageGroupPolicySnapshot;
    if (
      entry.mergePricesOverride !== undefined &&
      entry.mergePricesOverride !== null &&
      typeof entry.mergePricesOverride !== 'boolean'
    )
      throw new QuoteMapperError('报价内套餐合并设置无效');
    if (
      packageGroupSnapshot !== undefined &&
      packageGroupSnapshot !== null &&
      (!packageGroupSnapshot ||
        typeof packageGroupSnapshot !== 'object' ||
        typeof (packageGroupSnapshot as Record<string, unknown>).groupId !==
          'string' ||
        typeof (packageGroupSnapshot as Record<string, unknown>).revisionId !==
          'string' ||
        typeof (packageGroupSnapshot as Record<string, unknown>)
          .resourceVersion !== 'string' ||
        typeof (packageGroupSnapshot as Record<string, unknown>).name !==
          'string' ||
        typeof (packageGroupSnapshot as Record<string, unknown>).mergePrices !==
          'boolean' ||
        typeof (packageGroupSnapshot as Record<string, unknown>)
          .participatesInDiscount !== 'boolean')
    )
      throw new QuoteMapperError('报价内套餐组合快照无效');
    const members = entry.members.map((rawMember) => {
      if (!rawMember || typeof rawMember !== 'object')
        throw new QuoteMapperError('报价内套餐成员格式无效');
      const member = rawMember as Record<string, unknown>;
      if (
        typeof member.deviceId !== 'string' ||
        typeof member.quantity !== 'number' ||
        !Number.isSafeInteger(member.quantity) ||
        member.quantity < 1
      )
        throw new QuoteMapperError('报价内套餐成员字段无效');
      return { deviceId: member.deviceId, quantity: member.quantity };
    });
    return {
      id: entry.id,
      name: entry.name,
      sortOrder: entry.sortOrder,
      members,
      resourceVersion: entry.resourceVersion,
      ...(entry.mergePricesOverride !== undefined
        ? { mergePricesOverride: entry.mergePricesOverride as boolean | null }
        : {}),
      ...(packageGroupId !== undefined ? { packageGroupId } : {}),
      ...(packageGroupSnapshot !== undefined
        ? {
            packageGroupSnapshot:
              packageGroupSnapshot as QuoteLocalPackageDefinitionModel['packageGroupSnapshot'],
          }
        : {}),
    };
  });
  const localPlanDefinitions = value.localPlanDefinitions.map(
    mapLocalPlanDefinition,
  );
  if (
    new Set(localPackageDefinitions.map((item) => item.id)).size !==
      localPackageDefinitions.length ||
    new Set(localPlanDefinitions.map((item) => item.id)).size !==
      localPlanDefinitions.length
  )
    throw new QuoteMapperError('报价内定义标识重复');
  return { localPackageDefinitions, localPlanDefinitions };
}

function mapLocalPlanInstance(value: unknown): LocalPlanInstanceModel {
  if (!value || typeof value !== 'object')
    throw new QuoteMapperError('报价内方案实例格式无效');
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.id !== 'string' ||
    candidate.id.length === 0 ||
    typeof candidate.definitionId !== 'string' ||
    typeof candidate.definitionResourceVersion !== 'string' ||
    !/^[1-9][0-9]*$/.test(candidate.definitionResourceVersion) ||
    typeof candidate.nameSnapshot !== 'string' ||
    (typeof candidate.discountPercentSnapshot !== 'string' &&
      typeof candidate.discountPercentSnapshot !== 'number') ||
    !Number.isFinite(Number(candidate.discountPercentSnapshot)) ||
    Number(candidate.discountPercentSnapshot) < 0 ||
    typeof candidate.quantity !== 'number' ||
    !Number.isSafeInteger(candidate.quantity) ||
    candidate.quantity < 1 ||
    typeof candidate.businessModeCode !== 'string' ||
    !isStableBusinessModeCode(candidate.businessModeCode as BusinessModeCode) ||
    typeof candidate.resourceVersion !== 'string' ||
    !/^[1-9][0-9]*$/.test(candidate.resourceVersion) ||
    (candidate.status !== 'active' && candidate.status !== 'unbound') ||
    !Array.isArray(candidate.members)
  )
    throw new QuoteMapperError('报价内方案实例缺少合同字段');
  const members = candidate.members.map(
    (member): LocalPlanInstanceMemberModel => {
      if (!member || typeof member !== 'object')
        throw new QuoteMapperError('报价内方案实例成员格式无效');
      const entry = member as Record<string, unknown>;
      if (
        typeof entry.planMemberId !== 'string' ||
        typeof entry.occurrenceId !== 'string' ||
        typeof entry.quantityPerSet !== 'number' ||
        !Number.isSafeInteger(entry.quantityPerSet) ||
        entry.quantityPerSet < 1 ||
        !entry.sourceSnapshot ||
        typeof entry.sourceSnapshot !== 'object' ||
        !Array.isArray(entry.equipmentMembers)
      )
        throw new QuoteMapperError('报价内方案实例成员缺少合同字段');
      const source = entry.sourceSnapshot as Record<string, unknown>;
      let sourceSnapshot: LocalPlanInstanceMemberModel['sourceSnapshot'];
      if (
        source.type === 'catalog-package' &&
        typeof source.packageId === 'string' &&
        typeof source.packageRevisionId === 'string'
      )
        sourceSnapshot = {
          type: 'catalog-package',
          packageId: source.packageId,
          packageRevisionId: source.packageRevisionId,
        };
      else if (
        source.type === 'local-package' &&
        typeof source.definitionId === 'string' &&
        typeof source.definitionResourceVersion === 'string' &&
        /^[1-9][0-9]*$/.test(source.definitionResourceVersion)
      )
        sourceSnapshot = {
          type: 'local-package',
          definitionId: source.definitionId,
          definitionResourceVersion: source.definitionResourceVersion,
        };
      else throw new QuoteMapperError('报价内方案实例成员来源无效');
      const equipmentMembers = entry.equipmentMembers.map(
        (raw): LocalPlanInstanceMemberModel['equipmentMembers'][number] => {
          if (!raw || typeof raw !== 'object')
            throw new QuoteMapperError('方案实例设备清单格式无效');
          const equipment = raw as Record<string, unknown>;
          if (
            typeof equipment.deviceId !== 'string' ||
            typeof equipment.deviceRevisionId !== 'string' ||
            typeof equipment.quantityPerPackage !== 'number' ||
            !Number.isSafeInteger(equipment.quantityPerPackage) ||
            equipment.quantityPerPackage < 1 ||
            (equipment.packageRelationId !== null &&
              typeof equipment.packageRelationId !== 'string') ||
            (equipment.packageItemPriceCents !== null &&
              (typeof equipment.packageItemPriceCents !== 'number' ||
                !Number.isSafeInteger(equipment.packageItemPriceCents) ||
                equipment.packageItemPriceCents < 0)) ||
            typeof equipment.businessModeCode !== 'string' ||
            !isStableBusinessModeCode(
              equipment.businessModeCode as BusinessModeCode,
            ) ||
            typeof equipment.priceContextVersion !== 'string'
          )
            throw new QuoteMapperError('方案实例设备价格快照无效');
          return {
            deviceId: equipment.deviceId,
            deviceRevisionId: equipment.deviceRevisionId,
            quantityPerPackage: equipment.quantityPerPackage,
            packageRelationId: equipment.packageRelationId as string | null,
            packageItemPriceCents: equipment.packageItemPriceCents as
              | number
              | null,
            businessModeCode:
              equipment.businessModeCode as StableBusinessModeCode,
            priceContextVersion: equipment.priceContextVersion,
          };
        },
      );
      return {
        planMemberId: entry.planMemberId,
        occurrenceId: entry.occurrenceId,
        quantityPerSet: entry.quantityPerSet,
        sourceSnapshot,
        equipmentMembers,
      };
    },
  );
  if (
    new Set(members.map((member) => member.planMemberId)).size !==
      members.length ||
    new Set(members.map((member) => member.occurrenceId)).size !==
      members.length
  )
    throw new QuoteMapperError('报价内方案实例成员标识重复');
  return {
    id: candidate.id,
    definitionId: candidate.definitionId,
    definitionResourceVersion: candidate.definitionResourceVersion,
    nameSnapshot: candidate.nameSnapshot,
    discountPercentSnapshot: Number(candidate.discountPercentSnapshot),
    quantity: candidate.quantity,
    businessModeCode: candidate.businessModeCode as StableBusinessModeCode,
    status: candidate.status,
    resourceVersion: candidate.resourceVersion,
    members,
  };
}

function mapColumn(value: QuoteColumnSnapshot): QuoteColumnModel {
  const identity = value as QuoteColumnSnapshot & {
    id?: unknown;
    sourceColumnId?: unknown;
    definitionId?: string;
    definitionVersion?: number;
    readable?: boolean;
    exportable?: boolean;
  };
  if (
    (identity.id !== undefined && typeof identity.id !== 'string') ||
    (identity.sourceColumnId !== undefined &&
      identity.sourceColumnId !== null &&
      typeof identity.sourceColumnId !== 'string')
  )
    throw new QuoteMapperError('报价列身份格式无效');
  return {
    ...(typeof identity.id === 'string' ? { id: identity.id } : {}),
    ...(typeof identity.sourceColumnId === 'string' ||
    identity.sourceColumnId === null
      ? { sourceColumnId: identity.sourceColumnId }
      : {}),
    ...(typeof identity.definitionId === 'string'
      ? { definitionId: identity.definitionId }
      : {}),
    ...(typeof identity.definitionVersion === 'number'
      ? { definitionVersion: identity.definitionVersion }
      : {}),
    ...(typeof identity.readable === 'boolean'
      ? { readable: identity.readable }
      : {}),
    ...(typeof identity.exportable === 'boolean'
      ? { exportable: identity.exportable }
      : {}),
    fieldKey: value.fieldKey,
    labelZh: value.labelZh,
    valueType: value.valueType,
    sourceKind: value.sourceKind,
    sortOrder: value.sortOrder,
  };
}

export function mapQuoteManagementRecord(
  value: QuoteManagementRecord,
): QuoteManagementRecordModel {
  const createdBy = value.createdBy;
  if (
    createdBy !== null &&
    (typeof createdBy !== 'string' || !createdBy.trim())
  )
    throw new QuoteMapperError('报价管理列表缺少创建人信息，请核对服务端版本');
  if (value.createdByName !== null && typeof value.createdByName !== 'string')
    throw new QuoteMapperError('报价管理列表创建人名称无效，请核对服务端版本');
  if (typeof value.orderReady !== 'boolean')
    throw new QuoteMapperError('报价管理列表缺少待下单标记，请核对服务端版本');
  const share = value.currentCustomerShare;
  const amount = value.currentShareAmount;
  if (
    !['unshared', 'active', 'disabled', 'expired', 'revoked'].includes(
      value.customerLinkStatus,
    ) ||
    share === undefined ||
    amount === undefined
  )
    throw new QuoteMapperError(
      '报价管理列表缺少当前客户分享合同，请核对服务端版本',
    );
  if (
    value.customerLinkStatus === 'unshared'
      ? share !== null || amount !== null || value.fixedShareLinkId !== null
      : !share ||
        !amount ||
        !value.fixedShareLinkId ||
        share.quoteVersion?.quoteId !== value.id ||
        !/^[1-9][0-9]*$/.test(share.customerVersionNo) ||
        !share.publicationId ||
        amount.currency !== 'CNY' ||
        !Number.isSafeInteger(amount.totalCents) ||
        amount.totalCents < 0
  )
    throw new QuoteMapperError(
      '当前客户分享摘要绑定无效，未以草稿或未分享状态替代',
    );
  return {
    ...mapMaster(value),
    ...mapOrderSummary(value),
    currentCustomerShare: share
      ? {
          publicationId: share.publicationId,
          customerVersionNo: share.customerVersionNo,
          quoteVersion: {
            quoteId: share.quoteVersion.quoteId,
            quoteVersionId: share.quoteVersion.quoteVersionId,
            quoteVersionNo: share.quoteVersion.quoteVersionNo,
            resourceVersion: share.quoteVersion.resourceVersion,
          },
        }
      : null,
    currentShareAmount: amount
      ? { currency: amount.currency, totalCents: amount.totalCents }
      : null,
    customerLinkStatus: value.customerLinkStatus,
    customerName: value.customerName,
    createdByName: value.createdByName,
    customerManagerUserId: value.customerManagerUserId,
    createdBy,
    orderReady: value.orderReady,
    state: value.state,
    salesOrderId: value.salesOrderId,
  };
}

export function mapQuoteManagementPage(
  value: QuoteManagementPage,
): QuoteManagementRecordModel[] {
  return value.items.map(mapQuoteManagementRecord);
}

export function mapQuoteWorkspaceAggregate(
  value: QuoteWorkspaceAggregate,
): QuoteWorkspaceModel {
  const extended = value as QuoteWorkspaceAggregate & {
    localPlanDefinitions?: unknown[];
    localPlanInstances?: unknown[];
    containerInstances?: unknown;
    authorization?: QuoteWorkspaceModel['authorization'];
    containerEditModeSettings?: QuoteWorkspaceModel['containerEditModeSettings'];
  };
  const groups = value.groups.map(mapGroup);
  // The workspace aggregate owns the selected version's container tree.
  // Version-tree nodes carry metadata only, so reading the tree solely from
  // selectedVersion loses every persisted row/container association.
  const selectedVersion = mapQuoteVersionNode(
    extended.containerInstances === undefined
      ? value.selectedVersion
      : Object.assign({}, value.selectedVersion, {
          containerInstances: extended.containerInstances,
        }),
  );
  const localPlanDefinitions = (extended.localPlanDefinitions ?? []).map(
    mapLocalPlanDefinition,
  );
  const localPlanInstances = (extended.localPlanInstances ?? []).map(
    mapLocalPlanInstance,
  );
  if (
    new Set(localPlanDefinitions.map((definition) => definition.id)).size !==
      localPlanDefinitions.length ||
    new Set(localPlanInstances.map((instance) => instance.id)).size !==
      localPlanInstances.length
  )
    throw new QuoteMapperError('报价内方案标识重复');
  const packageDefinitionIds = new Set(
    (value.localPackageDefinitions ?? []).map((definition) => definition.id),
  );
  for (const definition of localPlanDefinitions)
    for (const member of definition.members)
      if (
        member.source.type === 'local-package' &&
        !packageDefinitionIds.has(member.source.definitionId)
      )
        throw new QuoteMapperError('报价内方案引用的本地套餐不存在');
  const instancesById = new Map(
    localPlanInstances.map((instance) => [instance.id, instance]),
  );
  const customizedLocalPlanRoots = new Set(
    selectedVersion.containerInstances
      .filter(
        (container) =>
          container.kind === 'plan' &&
          container.parentInstanceId === null &&
          container.parentMemberId === null &&
          container.originKind === 'quote-local-plan' &&
          container.status === 'customized' &&
          [...instancesById.values()].some(
            (instance) =>
              instance.id === container.id &&
              instance.status === 'active' &&
              instance.definitionId === container.originId &&
              instance.definitionResourceVersion ===
                (container.originResourceVersion ??
                  container.originSnapshot.resourceVersion),
          ),
      )
      .map((container) => container.id),
  );
  for (const group of groups) {
    if (!group.planInstanceId) continue;
    if (customizedLocalPlanRoots.has(group.planInstanceId)) continue;
    const instance = instancesById.get(group.planInstanceId);
    if (
      instance?.status !== 'active' ||
      !instance.members.some(
        (member) => member.planMemberId === group.planMemberId,
      )
    )
      throw new QuoteMapperError('报价组引用了不存在或已解绑的方案成员');
  }
  for (const instance of localPlanInstances) {
    if (instance.status !== 'active') continue;
    if (customizedLocalPlanRoots.has(instance.id)) continue;
    const memberIds = new Set(
      instance.members.map((member) => member.planMemberId),
    );
    const activeGroups = groups.filter(
      (group) => group.planInstanceId === instance.id,
    );
    if (
      activeGroups.length !== memberIds.size ||
      new Set(activeGroups.map((group) => group.planMemberId)).size !==
        memberIds.size ||
      [...memberIds].some(
        (memberId) =>
          !activeGroups.some((group) => group.planMemberId === memberId),
      )
    )
      throw new QuoteMapperError('活动报价内方案实例缺少完整成员分组');
  }
  const containerInstancesById = new Map(
    selectedVersion.containerInstances.map((instance) => [
      instance.id,
      instance,
    ]),
  );
  for (const instance of selectedVersion.containerInstances) {
    if (instance.parentInstanceId) {
      const parent = containerInstancesById.get(instance.parentInstanceId);
      if (
        instance.kind !== 'package' ||
        !instance.parentMemberId ||
        parent?.kind !== 'plan'
      )
        throw new QuoteMapperError('报价容器父级关系无效');
    } else if (instance.parentMemberId !== null) {
      throw new QuoteMapperError('报价容器方案成员关系无效');
    }
  }
  const groupsById = new Map(groups.map((group) => [group.id, group]));
  for (const item of value.items as Array<
    QuoteWorkspaceItem & { containerInstanceId?: string | null }
  >) {
    const containerInstanceId = item.containerInstanceId;
    if (containerInstanceId == null) continue;
    const container = containerInstancesById.get(containerInstanceId);
    const group = item.groupId ? groupsById.get(item.groupId) : undefined;
    if (
      container?.kind !== 'package' ||
      !group ||
      group.containerInstanceId !== container.id
    )
      throw new QuoteMapperError('报价行容器实例关联不完整');
  }
  for (const group of groups) {
    if (!group.containerInstanceId) continue;
    const container = containerInstancesById.get(group.containerInstanceId);
    if (container?.kind !== 'package')
      throw new QuoteMapperError('报价组容器实例不存在');
  }
  return {
    ...mapMaster(value),
    ...mapOrderSummary(value),
    allowedActions:
      'allowedActions' in value &&
      Array.isArray(value.allowedActions) &&
      value.allowedActions.every((action) => typeof action === 'string')
        ? [...value.allowedActions]
        : [],
    scopeAllowedPermissions: value.scopeAllowedPermissions
      ? [...value.scopeAllowedPermissions]
      : undefined,
    ...(extended.containerEditModeSettings
      ? { containerEditModeSettings: extended.containerEditModeSettings }
      : {}),
    ...(extended.authorization
      ? { authorization: extended.authorization }
      : {}),
    confirmationHistory: (value.confirmationHistory ?? []).map((entry) => ({
      confirmationId: requiredString(entry.confirmationId, '确认记录标识'),
      quoteVersionId: requiredString(entry.quoteVersionId, '确认版本标识'),
      quoteVersionNo: String(entry.quoteVersionNo),
      confirmedAt: requiredString(entry.confirmedAt, '确认时间'),
      submissionStatus: entry.submissionStatus,
      deliveryStatus: entry.deliveryStatus,
    })),
    selectedVersion,
    pricingView: value.pricingView,
    calculationSnapshot: {
      currency: value.calculationSnapshot.currency,
      discountPercent: value.calculationSnapshot.discountPercent,
      modeOverallDiscounts: value.calculationSnapshot.modeOverallDiscounts,
      totalCents: value.calculationSnapshot.totalCents,
    },
    items: value.items.map(mapWorkspaceItem),
    groups,
    localPackageDefinitions: mapQuoteLocalDefinitions({
      localPackageDefinitions: value.localPackageDefinitions ?? [],
      localPlanDefinitions: [],
    }).localPackageDefinitions,
    localPlanDefinitions,
    localPlanInstances,
    columns: value.columns.map(mapColumn),
    selectableColumns: (value.selectableColumns ?? []).map(mapColumn),
  };
}

export function quoteWorkspaceAllowsAction(
  workspace: QuoteWorkspaceModel | null | undefined,
  quoteId: string | undefined,
  action: string,
  versionId?: string,
): boolean {
  return Boolean(
    workspace &&
      workspace.id === quoteId &&
      (!versionId || workspace.selectedVersion.id === versionId) &&
      Array.isArray(workspace.allowedActions) &&
      workspace.allowedActions.includes(action),
  );
}

export function mapQuoteVersionTree(value: QuoteVersionTree) {
  return {
    quoteId: value.quoteId,
    currentPublishedVersion: mapTuple(value.currentPublishedVersion),
    versions: value.versions.map(mapQuoteVersionNode),
  };
}

function mapWarning(value: InventoryWarning): QuoteWarningModel {
  if (value.code === 'INVENTORY_UNKNOWN')
    return {
      code: value.code,
      severity: value.severity,
      status: value.status,
      inventoryFreshnessRunId: value.inventoryFreshnessRunId,
      reason: value.reason,
      items: value.items.map((item) => ({
        quoteItemId: item.quoteItemId,
        deviceId: item.deviceId,
        warehouseId: item.warehouseId,
        inventoryFreshnessRunId: item.inventoryFreshnessRunId,
        status: item.status,
        requiredQuantity: item.requiredQuantity,
        availableQuantity: item.availableQuantity,
        observedAt: item.observedAt,
        qualityReason: item.qualityReason,
      })),
    };
  return {
    code: value.code,
    severity: value.severity,
    status: value.status,
    items: value.items.map((item) => ({
      quoteItemId: item.quoteItemId,
      deviceId: item.deviceId,
      warehouseId: item.warehouseId,
      inventoryFreshnessRunId: item.inventoryFreshnessRunId,
      status: item.status,
      requiredQuantity: item.requiredQuantity,
      availableQuantity: item.availableQuantity,
      observedAt: item.observedAt,
      qualityReason: item.qualityReason,
    })),
  };
}

export function mapQuoteMutationResult(value: {
  quoteMaster: QuoteMasterRecord;
  version: QuoteVersionNode;
  inventoryWarnings?: InventoryWarning[];
  replayed: boolean;
}): QuoteMutationModel {
  return {
    quoteMaster: mapMaster(value.quoteMaster),
    version: mapQuoteVersionNode(value.version),
    inventoryWarnings: value.inventoryWarnings?.map(mapWarning),
    replayed: value.replayed,
  };
}

let lastQuoteResourceId = 0n;

export function nextQuoteResourceId(): string {
  const now = BigInt(Date.now());
  const random =
    typeof crypto !== 'undefined' && 'getRandomValues' in crypto
      ? crypto.getRandomValues(new Uint32Array(1))[0] % 1_000_000
      : Math.floor(Math.random() * 1_000_000);
  const candidate = now * 1_000_000n + BigInt(random);
  lastQuoteResourceId =
    candidate > lastQuoteResourceId ? candidate : lastQuoteResourceId + 1n;
  return lastQuoteResourceId.toString();
}

function baseDraftItem(
  input: Omit<
    QuoteWorkspaceDraftItem,
    'id' | 'unitPriceCents' | 'lineTotalCents'
  >,
): QuoteWorkspaceDraftItem {
  return {
    ...input,
    id: nextQuoteResourceId(),
    unitPriceCents: null,
    lineTotalCents: null,
  };
}

function activeRevisionId(
  value: string | null | undefined,
  label: string,
): string {
  return requiredString(value, `${label}当前 revision`);
}

function catalogStatus(value: string | null | undefined, label: string): void {
  if (value !== 'active')
    throw new QuoteMapperError(`${label}已停用，不能加入报价`);
}

export function buildEquipmentQuoteItem(
  equipment: Equipment,
  sortOrder: number,
): QuoteWorkspaceDraftItem {
  catalogStatus(equipment.status, '设备');
  const name = requiredString(equipment.name, '设备名称');
  return baseDraftItem({
    sourceType: 'equipment',
    sourceId: equipment.id,
    sourceRevisionId: activeRevisionId(equipment.currentRevisionId, '设备'),
    deviceId: equipment.id,
    sourceSnapshot: {
      name,
      code: requiredString(equipment.equipmentCode, '设备编码'),
    },
    deviceFieldSnapshot: {
      category: equipment.category ?? null,
      sku: equipment.skuCode ?? null,
      jkyCode: equipment.jkyCode ?? null,
    },
    packageRelationId: null,
    packageId: null,
    packageItemPriceSnapshotCents: null,
    packageRelationQuantitySnapshot: null,
    planId: null,
    planDiscountSnapshot: null,
    planProductPriceSnapshotCents: null,
    replacementRelationId: null,
    quantity: 1,
    discountPercent: '100.00',
    sortOrder,
    remark: null,
    groupId: null,
  });
}

export function buildPackageQuoteItems(
  detail: PackageDetail,
  startSortOrder: number,
): QuoteWorkspaceDraftItem[] {
  catalogStatus(detail.status, '套餐');
  const name = requiredString(detail.revision.name, '套餐名称');
  const code = requiredString(detail.code, '套餐编码');
  const revisionId = activeRevisionId(detail.currentRevisionId, '套餐');
  const items = detail.items ?? [];
  if (items.length === 0)
    throw new QuoteMapperError('套餐没有可加入报价的设备关系');
  return items.map((item, index) => {
    if (item.packageItemMonthlyPriceCents === undefined)
      throw new QuoteMapperError('套餐关系缺少服务端单品月价');
    return baseDraftItem({
      sourceType: 'package',
      sourceId: detail.id,
      sourceRevisionId: revisionId,
      deviceId: item.deviceId,
      sourceSnapshot: {
        name,
        code,
        ...(item.replacementPoolId
          ? { replacementPoolId: item.replacementPoolId }
          : {}),
      },
      deviceFieldSnapshot: {
        name: item.deviceNameSnapshot,
        sku: item.deviceSkuSnapshot,
      },
      packageRelationId: item.relationId,
      packageId: detail.id,
      packageItemPriceSnapshotCents: item.packageItemMonthlyPriceCents,
      packageRelationQuantitySnapshot: item.quantity,
      planId: null,
      planDiscountSnapshot: null,
      planProductPriceSnapshotCents: null,
      replacementRelationId: null,
      quantity: item.quantity,
      discountPercent: '100.00',
      sortOrder: startSortOrder + index,
      remark: null,
      groupId: null,
    });
  });
}

function planItem(
  plan: PlanDetail,
  item: PlanPackageItemDetail,
  sortOrder: number,
): QuoteWorkspaceDraftItem {
  if (
    item.packageItemMonthlyPriceCents === undefined ||
    item.planUnitMonthlyPriceCents === undefined
  )
    throw new QuoteMapperError('方案关系缺少服务端单价快照');
  return baseDraftItem({
    sourceType: 'plan',
    sourceId: plan.planId,
    sourceRevisionId: activeRevisionId(plan.currentRevisionId, '方案'),
    deviceId: item.deviceId,
    sourceSnapshot: {
      name: requiredString(plan.name, '方案名称'),
      code: requiredString(plan.code, '方案编码'),
      // A plan may contain two packages with the same display name. Keep the
      // exact package revision on every flattened quote row so downgrade,
      // customer share and order confirmation never have to resolve by name
      // or package relation alone.
      packageRevisionId: item.packageRevisionId,
      ...(item.replacementPoolId
        ? { replacementPoolId: item.replacementPoolId }
        : {}),
    },
    deviceFieldSnapshot: {},
    packageRelationId: item.packageRelationId,
    packageId: item.packageId,
    packageItemPriceSnapshotCents: item.packageItemMonthlyPriceCents,
    packageRelationQuantitySnapshot: item.quantity,
    planId: plan.planId,
    planDiscountSnapshot: requiredString(plan.discount, '方案折扣'),
    planProductPriceSnapshotCents: item.planUnitMonthlyPriceCents,
    replacementRelationId: null,
    quantity: item.quantity,
    discountPercent: '100.00',
    sortOrder,
    remark: null,
    groupId: null,
  });
}

export function buildPlanQuoteItems(
  detail: PlanDetail,
  startSortOrder: number,
): QuoteWorkspaceDraftItem[] {
  catalogStatus(detail.status, '方案');
  const packages = detail.packages ?? [];
  const items = packages.flatMap((pkg) => pkg.items);
  if (items.length === 0)
    throw new QuoteMapperError('方案没有可加入报价的设备关系');
  return items.map((item, index) =>
    planItem(detail, item, startSortOrder + index),
  );
}

export function draftItemsFromWorkspace(
  workspace: QuoteWorkspaceModel,
): QuoteWorkspaceDraftItem[] {
  return workspace.items.map((item) => ({
    id: item.id,
    sourceType: item.sourceType,
    sourceId: item.sourceId,
    sourceRevisionId: item.sourceRevisionId,
    deviceId: item.deviceId,
    sourceSnapshot: item.sourceSnapshot,
    deviceFieldSnapshot: item.deviceFieldSnapshot,
    packageRelationId: item.packageRelationId,
    packageId: item.packageId,
    packageItemPriceSnapshotCents:
      item.sourceType === 'equipment'
        ? null
        : item.packageItemPriceSnapshotCents,
    packageRelationQuantitySnapshot:
      item.sourceType === 'equipment'
        ? null
        : item.packageRelationQuantitySnapshot,
    planId: item.planId,
    planDiscountSnapshot: item.planDiscountSnapshot,
    planProductPriceSnapshotCents: item.planProductPriceSnapshotCents,
    replacementRelationId: item.replacementRelationId ?? null,
    quantity: item.quantity,
    discountPercent: item.discountPercent,
    sortOrder: item.sortOrder,
    remark: item.remark,
    groupId: item.groupId,
    ...(item.containerInstanceId === undefined
      ? {}
      : { containerInstanceId: item.containerInstanceId }),
    unitPriceCents: item.unitPriceCents,
    lineTotalCents: item.lineTotalCents,
  }));
}

type QuoteWorkspaceItemWriteWithExclusivePricingEdit =
  QuoteWorkspaceItemWrite & {
    exclusivePricingEdit?: 'exclusive_discount' | 'exclusive_price';
    exclusivePricingEditContext?: {
      itemIds: string[];
      totalLineCents: number;
    };
  };

function saveItem(
  item: QuoteWorkspaceDraftItem,
): QuoteWorkspaceItemWriteWithExclusivePricingEdit {
  const deviceFieldSnapshot = { ...item.deviceFieldSnapshot };
  const localPricing = deviceFieldSnapshot.quoteLocalPricing;
  if (
    localPricing &&
    typeof localPricing === 'object' &&
    !Array.isArray(localPricing)
  ) {
    // 数量往返的基准只留在编辑状态；正式合同只接收已计算的本单覆盖值。
    deviceFieldSnapshot.quoteLocalPricing = Object.fromEntries(
      Object.entries(localPricing).filter(
        ([key]) =>
          key !== 'exclusiveLineBasisCents' &&
          key !== 'exclusiveLineBasisQuantity',
      ),
    );
  }
  return {
    id: item.id,
    sourceType: item.sourceType,
    sourceId: item.sourceId,
    sourceRevisionId: item.sourceRevisionId,
    deviceId: item.deviceId,
    sourceSnapshot: item.sourceSnapshot,
    deviceFieldSnapshot,
    packageRelationId:
      item.sourceType === 'equipment' ? null : item.packageRelationId,
    packageId: item.sourceType === 'equipment' ? null : item.packageId,
    packageItemPriceSnapshotCents:
      item.sourceType === 'equipment'
        ? null
        : item.packageItemPriceSnapshotCents,
    packageRelationQuantitySnapshot:
      item.sourceType === 'equipment'
        ? null
        : item.packageRelationQuantitySnapshot,
    // 本报价方案父级由活动实例及组表达；不能伪装成目录方案来源。
    planId: item.sourceType === 'plan' ? item.planId : null,
    planDiscountSnapshot:
      item.sourceType === 'plan' ? item.planDiscountSnapshot : null,
    planProductPriceSnapshotCents:
      item.sourceType === 'plan' ? item.planProductPriceSnapshotCents : null,
    replacementRelationId: item.replacementRelationId ?? null,
    quantity: item.quantity,
    discountPercent: item.discountPercent,
    sortOrder: item.sortOrder,
    remark: item.remark,
    groupId: item.groupId,
    ...(item.containerInstanceId === undefined
      ? {}
      : { containerInstanceId: item.containerInstanceId }),
    ...(item.exclusivePricingEdit
      ? { exclusivePricingEdit: item.exclusivePricingEdit }
      : {}),
    ...(item.exclusivePricingEditContext
      ? {
          exclusivePricingEditContext: {
            itemIds: [...item.exclusivePricingEditContext.itemIds],
            totalLineCents: item.exclusivePricingEditContext.totalLineCents,
          },
        }
      : {}),
  };
}

export function unknownInventoryForDraftItems(
  _items: readonly QuoteWorkspaceDraftItem[],
): InventoryFreshnessBinding {
  return {
    inventoryFreshnessRunId: null,
    status: 'UNKNOWN',
    observedAt: null,
    items: [],
  } as InventoryFreshnessBinding;
}

function inventoryItemRequest(item: QuoteInventoryItemModel) {
  if (item.status === 'UNKNOWN') {
    return {
      quoteItemId: item.quoteItemId,
      deviceId: item.deviceId,
      warehouseId: item.warehouseId,
      inventoryFreshnessRunId: item.inventoryFreshnessRunId,
      status: item.status,
      requiredQuantity: item.requiredQuantity,
      availableQuantity: null,
      observedAt: item.observedAt,
      qualityReason: item.qualityReason ?? 'INVENTORY_UNKNOWN',
    };
  }
  if (
    item.inventoryFreshnessRunId === null ||
    item.observedAt === null ||
    item.availableQuantity === null
  )
    throw new QuoteMapperError(`报价行 ${item.quoteItemId} 的库存事实不完整`);
  return {
    quoteItemId: item.quoteItemId,
    deviceId: item.deviceId,
    warehouseId: item.warehouseId,
    inventoryFreshnessRunId: item.inventoryFreshnessRunId,
    status: item.status,
    requiredQuantity: item.requiredQuantity,
    availableQuantity: item.availableQuantity,
    observedAt: item.observedAt,
    qualityReason: null,
  };
}

export function inventoryFreshnessRequest(
  value: QuoteInventoryModel | undefined,
): InventoryFreshnessBinding {
  if (!value)
    throw new QuoteMapperError('没有读取报价库存事实的权限，不能准备导出绑定');
  if (
    value.status !== 'UNKNOWN' &&
    (value.inventoryFreshnessRunId === null || value.observedAt === null)
  )
    throw new QuoteMapperError('报价库存聚合事实不完整');
  return {
    inventoryFreshnessRunId: value.inventoryFreshnessRunId,
    status: value.status,
    observedAt: value.observedAt,
    items: value.items.map(inventoryItemRequest),
  } as InventoryFreshnessBinding;
}

export function workspaceSnapshot(
  quoteId: string,
  versionId: string,
  versionNo: string,
): FrozenProjectionSnapshot {
  const parsedVersionNo = Number(versionNo);
  if (!Number.isSafeInteger(parsedVersionNo) || parsedVersionNo < 1)
    throw new QuoteMapperError('报价版本号不是有效的安全整数');
  return {
    revisionTuple: {
      quoteId,
      quoteVersionId: versionId,
      quoteVersionNo: parsedVersionNo,
      sourceHash: '0'.repeat(64),
      calculationVersion: 'quote-workspace-cents-v1',
      fieldProjectionVersion: 'quote-columns-v1',
      templateVersion: 'quote-template-v1',
    },
    fieldProjection: { fields: [], channel: 'internal-share' },
    sourceSnapshot: { kind: 'quote-workspace-empty-draft' },
  };
}

export function frozenSnapshotRequest(
  workspace: QuoteWorkspaceModel,
): FrozenProjectionSnapshot {
  const snapshot = workspace.selectedVersion.frozenSnapshot;
  if (snapshot === undefined)
    throw new QuoteMapperError(
      '没有读取报价冻结快照的权限，不能从隐藏数据重建报价',
    );
  if (snapshot === null)
    return workspaceSnapshot(
      workspace.id,
      workspace.selectedVersion.id,
      workspace.selectedVersion.versionNo,
    );
  return {
    revisionTuple: { ...snapshot.revisionTuple },
    fieldProjection: {
      fields: [...snapshot.fieldProjection.fields],
      channel: snapshot.fieldProjection.channel,
    },
    sourceSnapshot: { ...snapshot.sourceSnapshot },
  };
}

export function saveDraftRequest(input: {
  workspace: QuoteWorkspaceModel;
  customerId?: string | null;
  pendingCustomerName?: string | null;
  items: readonly QuoteWorkspaceDraftItem[];
  groups?: readonly QuoteGroupModel[];
  localPackageDefinitions?: readonly QuoteLocalPackageDefinitionModel[];
  localPlanInstances?: readonly LocalPlanInstanceModel[];
  columns?: readonly QuoteColumnModel[];
  pricingView: 'purchase' | 'rental';
  businessModeCode?: StableBusinessModeCode;
  quoteDiscountPercent: string;
  quoteExpiresAt?: string | null;
  marketingDescription?: string;
  marketingDescriptionEdited?: boolean;
  outputTermsDisplayOverrides?: OutputTermsDisplayOverrides;
  outputTermsRequired?: boolean;
  containerEditMode?: 'legacy_unbind' | 'quote_edit';
  containerInstances?: readonly QuoteContainerInstance[];
}): SaveQuoteDraftRequest & {
  containerEditMode?: 'legacy_unbind' | 'quote_edit';
  containerInstances?: QuoteContainerInstanceWrite[];
  items: Array<
    QuoteWorkspaceItemWriteWithExclusivePricingEdit & {
      containerInstanceId?: string | null;
    }
  >;
  expectedContainerEditModeVersion?: string;
  groups: Array<QuoteGroupModel & { containerInstanceId?: string | null }>;
} {
  if (input.workspace.masterLocked)
    throw new QuoteMapperError(CONFIRMED_QUOTE_READONLY_MESSAGE);
  const columns = input.columns ?? input.workspace.columns;
  if (
    columns.some(
      (column) =>
        column.sourceKind === 'system' &&
        (typeof column.sourceColumnId !== 'string' ||
          column.sourceColumnId.trim() === ''),
    )
  )
    throw new QuoteMapperError(
      '报价系统字段缺少真实字段池来源，未保存，请重新选择字段',
    );
  // A newly opened quote has no selected customer yet.  The frozen contract
  // treats an omitted customer as valid, but rejects an empty BusinessId.
  // Keep other non-empty values intact so contract validation still exposes
  // genuinely invalid caller input instead of silently changing it.
  const customerId = input.customerId === '' ? undefined : input.customerId;
  const draftSnapshot = frozenSnapshotRequest(input.workspace);
  if (input.marketingDescription !== undefined)
    draftSnapshot.sourceSnapshot.marketingDescription =
      input.marketingDescription;
  if (input.marketingDescriptionEdited !== undefined)
    draftSnapshot.sourceSnapshot.marketingDescriptionEdited =
      input.marketingDescriptionEdited;
  if (input.outputTermsDisplayOverrides !== undefined)
    draftSnapshot.sourceSnapshot.outputTermsDisplayOverrides =
      input.outputTermsDisplayOverrides;
  if (input.outputTermsRequired !== undefined)
    draftSnapshot.sourceSnapshot.outputTermsRequired =
      input.outputTermsRequired;
  if (input.businessModeCode !== undefined)
    draftSnapshot.sourceSnapshot.businessModeCode = input.businessModeCode;
  const referencedGroupIds = new Set(
    input.items.flatMap((item) => (item.groupId ? [item.groupId] : [])),
  );
  const localPackageDefinitions =
    input.localPackageDefinitions ??
    input.workspace.localPackageDefinitions ??
    [];
  const hasDefinitionForEmptyLocalPackage = (group: QuoteGroupModel) =>
    (input.containerEditMode ??
      input.workspace.selectedVersion.containerEditMode) !== 'quote_edit' &&
    group.kind === 'quote-local-package' &&
    typeof group.definitionId === 'string' &&
    group.members?.length === 0 &&
    localPackageDefinitions.filter(
      (definition) =>
        definition.id === group.definitionId && definition.members.length === 0,
    ).length === 1;
  const containerInstances =
    input.containerInstances ??
    input.workspace.selectedVersion.containerInstances ??
    [];
  const packageInstanceIds = new Set(
    containerInstances
      .filter((instance) => instance.kind === 'package')
      .map((instance) => instance.id),
  );
  const groups = input.groups ?? input.workspace.groups;
  if (
    groups.some(
      (group) =>
        group.containerInstanceId &&
        !packageInstanceIds.has(group.containerInstanceId),
    )
  )
    throw new QuoteMapperError('当前报价分组引用了不存在的套餐实例');
  return {
    ...(input.workspace.containerEditModeSettings
      ? {
          expectedContainerEditModeVersion:
            input.workspace.containerEditModeSettings.resourceVersion,
        }
      : {}),
    ...(input.workspace.containerEditModeSettings !== undefined ||
    input.workspace.selectedVersion.containerEditModeSupported
      ? {
          containerEditMode:
            input.workspace.containerEditModeSettings === undefined
              ? (input.containerEditMode ??
                input.workspace.selectedVersion.containerEditMode)
              : input.workspace.containerEditModeSettings.enabled
                ? 'quote_edit'
                : 'legacy_unbind',
        }
      : {}),
    ...(customerId === undefined ? {} : { customerId }),
    ...(input.pendingCustomerName === undefined
      ? {}
      : { pendingCustomerName: input.pendingCustomerName }),
    ...(input.quoteExpiresAt === undefined
      ? {}
      : { quoteExpiresAt: input.quoteExpiresAt }),
    draftResourceVersion: input.workspace.selectedVersion.resourceVersion,
    draftSnapshot,
    pricingPolicyVersion: requiredString(
      input.workspace.selectedVersion.pricingPolicyVersion,
      '报价计价版本',
    ),
    pricingView: input.pricingView,
    ...(input.businessModeCode === undefined
      ? {}
      : { businessModeCode: input.businessModeCode }),
    quoteDiscountPercent: input.quoteDiscountPercent,
    items: input.items.map(saveItem),
    groups: groups
      .filter(
        (group) =>
          (group.containerInstanceId !== undefined &&
            group.containerInstanceId !== null) ||
          (group.kind !== 'quote-local-package' && !group.planInstanceId) ||
          referencedGroupIds.has(group.id) ||
          hasDefinitionForEmptyLocalPackage(group),
      )
      .map((group) => ({
        ...group,
        kind: group.kind ?? 'display',
        members:
          group.kind === 'quote-local-package' ? group.members : undefined,
      })) as SaveQuoteDraftRequest['groups'],
    ...(containerInstances.length > 0 ||
    input.containerEditMode === 'quote_edit'
      ? {
          containerInstances: containerInstances.map(
            ({ originSnapshot: _originSnapshot, ...instance }) => ({
              ...instance,
            }),
          ),
        }
      : {}),
    localPackageDefinitions: localPackageDefinitions.map(
      quoteLocalPackageDefinitionWrite,
    ),
    localPlanInstances: (
      input.localPlanInstances ??
      input.workspace.localPlanInstances ??
      []
    )
      .filter(
        (instance) =>
          instance.status !== 'active' ||
          // An edited plan may retain zero children. Its current quote-only
          // container still owns the original local-plan membership snapshot.
          ((input.containerEditMode ??
            input.workspace.selectedVersion.containerEditMode) ===
            'quote_edit' &&
            (
              input.containerInstances ??
              input.workspace.selectedVersion.containerInstances ??
              []
            ).some(
              (container) =>
                container.id === instance.id &&
                container.kind === 'plan' &&
                container.parentInstanceId === null &&
                container.parentMemberId === null &&
                container.originKind === 'quote-local-plan' &&
                container.status === 'customized' &&
                container.originId === instance.definitionId &&
                (container.originResourceVersion ??
                  container.originSnapshot.resourceVersion) ===
                  instance.definitionResourceVersion,
            )) ||
          input.workspace.localPlanInstances?.some(
            (stored) => stored.id === instance.id,
          ) ||
          (input.groups ?? input.workspace.groups).some(
            (group) =>
              group.planInstanceId === instance.id &&
              referencedGroupIds.has(group.id),
          ),
      )
      .map((instance) => ({
        ...structuredClone(instance),
        discountPercentSnapshot: instance.discountPercentSnapshot.toFixed(2),
      })),
    columns: columns.map(
      ({
        readable: _readable,
        exportable: _exportable,
        definitionId: _definitionId,
        definitionVersion: _definitionVersion,
        ...column
      }) => ({ ...column }),
    ),
    inventoryFreshness: unknownInventoryForDraftItems(input.items),
  };
}

export function exactVersionTuple(
  version: QuoteVersionModel,
): ExactQuoteVersionTuple {
  return {
    quoteId: version.quoteId,
    quoteVersionId: version.id,
    quoteVersionNo: version.versionNo,
    resourceVersion: version.resourceVersion,
  };
}
